import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import "./PhotentraAI.css";
import { clearAuthStorage } from "./auth/logout";

function PhotentraAI() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [convSidebarOpen, setConvSidebarOpen] = useState(window.innerWidth > 768);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [panelTab, setPanelTab] = useState("reminders");
  // Confirm/discard state for the CRM changes the assistant has proposed,
  // keyed by pending action id.
  const [pendingState, setPendingState] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const token = localStorage.getItem("token");
  const userEmail = localStorage.getItem("userEmail") || "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login");
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, loading, scrollToBottom]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/conversations`, { headers });
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (e) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Load reminders & appointments
  const loadReminders = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/reminders`, { headers });
      if (res.ok) { const data = await res.json(); setReminders(data.reminders || []); }
    } catch (e) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAppointments = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/appointments`, { headers });
      if (res.ok) { const data = await res.json(); setAppointments(data.appointments || []); }
    } catch (e) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadReminders(); loadAppointments(); }, [loadReminders, loadAppointments]);

  // Load messages for active conversation
  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/conversations/${convId}/messages`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectConversation = (convId) => {
    setActiveConvId(convId);
    loadMessages(convId);
    if (window.innerWidth <= 768) setConvSidebarOpen(false);
  };

  const startNewChat = () => {
    setActiveConvId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const deleteConversation = async (e, convId) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE_URL}/api/ai/conversations/${convId}`, { method: "DELETE", headers });
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConvId === convId) {
        setActiveConvId(null);
        setMessages([]);
      }
    } catch (e) { /* ignore */ }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setLoading(true);

    // Optimistically add user message
    const userMsg = { id: Date.now(), role: "user", content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);

    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({ message: text, conversationId: activeConvId })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Request failed");
      }

      const data = await res.json();

      // Set conversation ID if new
      if (!activeConvId) {
        setActiveConvId(data.conversationId);
        loadConversations();
      }

      // Add assistant message
      const assistantMsg = {
        id: Date.now() + 1,
        role: "assistant",
        content: data.response,
        // A proposal arrives beside the reply rather than inside actionData, so a
        // turn can both save an invoice and propose a CRM change. The same
        // precedence the server uses when it stores the message: a saved action
        // outranks a suggested one.
        action_type: data.actionType
          || (data.pendingActions && data.pendingActions.length ? "proposal" : null),
        action_data: data.actionData
          || (data.pendingActions && data.pendingActions.length
            ? { pending_actions: data.pendingActions } : null),
        saved_record: data.savedRecord,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Refresh panel data if an action was saved
      if (data.actionType === 'reminder') loadReminders();
      if (data.actionType === 'appointment') loadAppointments();
    } catch (err) {
      const errorMsg = {
        id: Date.now() + 1,
        role: "assistant",
        content: `Sorry, something went wrong: ${err.message}`,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions = [
    "Best camera settings for low light?",
    "Book a shoot tomorrow at 5pm for Arjun",
    "Remind me about editing tonight at 9",
    "Tips for outdoor wedding photography"
  ];

  // Where a proposed CRM change is accepted or thrown away. Keyed by the pending
  // action's id so two proposals in one reply resolve independently.
  const resolvePending = async (id, decision) => {
    setPendingState(prev => ({ ...prev, [id]: { ...prev[id], busy: true, error: null } }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/pending-actions/${id}/${decision}`, {
        method: "POST",
        headers,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || "That didn't work");
      setPendingState(prev => ({
        ...prev,
        [id]: { busy: false, status: decision === "confirm" ? "confirmed" : "cancelled" },
      }));
    } catch (err) {
      setPendingState(prev => ({ ...prev, [id]: { busy: false, error: err.message } }));
    }
  };

  // Nothing here has happened yet. The wording says so, and the card keeps
  // saying so until the studio picks one of the two buttons.
  const renderProposalCard = (msg) => {
    const proposals = (msg.action_data && msg.action_data.pending_actions) || [];
    if (proposals.length === 0) return null;

    return (
      <div className="ai-proposal-list">
        {proposals.map((p) => {
          const state = pendingState[p.id] || {};
          const settled = state.status || (p.status && p.status !== "pending" ? p.status : null);
          return (
            <div key={p.id} className={`ai-proposal ${settled ? `is-${settled}` : ""}`}>
              <div className="ai-proposal-head">
                {settled === "confirmed" ? "✅ Saved" : settled === "cancelled" ? "🗑️ Discarded" : "📝 Needs your OK"}
              </div>
              <div className="ai-proposal-body">{p.summary}</div>
              {!settled && (
                <div className="ai-proposal-actions">
                  <button
                    className="ai-proposal-confirm"
                    disabled={state.busy}
                    onClick={() => resolvePending(p.id, "confirm")}
                  >
                    {state.busy ? "Saving…" : "Save it"}
                  </button>
                  <button
                    className="ai-proposal-cancel"
                    disabled={state.busy}
                    onClick={() => resolvePending(p.id, "cancel")}
                  >
                    Discard
                  </button>
                </div>
              )}
              {state.error && <div className="ai-proposal-error">{state.error}</div>}
            </div>
          );
        })}
      </div>
    );
  };

  const renderActionCard = (msg) => {
    const data = msg.action_data;
    if (!data) return null;

    const typeLabels = { appointment: "📅 Appointment Created", reminder: "⏰ Reminder Set", invoice: "🧾 Invoice Generated" };

    return (
      <div className="ai-action-card">
        <div className="ai-action-card-header">
          {typeLabels[data.type] || "📋 Action"}
        </div>
        <div className="ai-action-card-body">
          {data.title && <div className="ai-action-field"><strong>Title:</strong> {data.title}</div>}
          {data.client_name && <div className="ai-action-field"><strong>Client:</strong> {data.client_name}</div>}
          {data.date && <div className="ai-action-field"><strong>Date:</strong> {data.date}</div>}
          {data.time && <div className="ai-action-field"><strong>Time:</strong> {data.time}</div>}
          {data.amount && <div className="ai-action-field"><strong>Amount:</strong> {data.amount}</div>}
          {data.description && <div className="ai-action-field"><strong>Desc:</strong> {data.description}</div>}
        </div>
        {msg.saved_record && (
          <div className="ai-saved-badge">✅ Saved to database</div>
        )}
      </div>
    );
  };

  return (
    <div className="ai-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="ai-main">
        {/* Header */}
        <header className="ai-header">
          <div className="ai-header-left">
            <button className="ai-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="ai-page-title">🤖 Photentra AI</h1>
          </div>
          <div className="ai-header-right">
            <button
              className="ai-panel-toggle"
              onClick={() => setShowPanel(!showPanel)}
              title="Reminders & Appointments"
            >
              📋 {reminders.length + appointments.length > 0 && <span className="ai-panel-badge">{reminders.length + appointments.length}</span>}
            </button>
            <span className="ai-user-email">{userEmail}</span>
            <button className="ai-logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </header>

        <div className="ai-body">
          {/* Conversation list sidebar */}
          <div className={`ai-conv-sidebar ${convSidebarOpen ? "open" : ""}`}>
            <div className="ai-conv-header">
              <button className="ai-new-chat-btn" onClick={startNewChat}>
                ✨ New Chat
              </button>
            </div>
            <div className="ai-conv-list">
              {conversations.map(conv => (
                <div
                  key={conv.id}
                  className={`ai-conv-item ${activeConvId === conv.id ? "active" : ""}`}
                  onClick={() => selectConversation(conv.id)}
                >
                  <span>💬</span>
                  <span className="ai-conv-item-text">{conv.title}</span>
                  <button className="ai-conv-delete" onClick={(e) => deleteConversation(e, conv.id)} title="Delete">🗑️</button>
                </div>
              ))}
              {conversations.length === 0 && (
                <div style={{ padding: "20px 12px", textAlign: "center", color: "#999", fontSize: 13 }}>
                  No conversations yet
                </div>
              )}
            </div>
          </div>

          {/* Chat area */}
          <div className="ai-chat-area">
            <div className="ai-messages">
              {messages.length === 0 && !loading ? (
                <div className="ai-welcome">
                  <div className="ai-welcome-icon">🤖</div>
                  <h3>Photentra AI Assistant</h3>
                  <p>
                    I'm your photography studio assistant. Ask me anything about photography,
                    or let me help you create appointments, set reminders, and generate invoices.
                  </p>
                  <div className="ai-suggestions">
                    {suggestions.map((s, i) => (
                      <button key={i} className="ai-suggestion-chip" onClick={() => { setInput(s); inputRef.current?.focus(); }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map(msg => (
                  <div key={msg.id} className={`ai-message ${msg.role}`}>
                    <div className="ai-message-avatar">
                      {msg.role === "user" ? "👤" : "🤖"}
                    </div>
                    <div>
                      <div className="ai-message-bubble">{msg.content}</div>
                      {msg.role === "assistant" && msg.action_data && (
                        msg.action_type === "proposal" ? renderProposalCard(msg) : renderActionCard(msg)
                      )}
                    </div>
                  </div>
                ))
              )}

              {loading && (
                <div className="ai-typing">
                  <div className="ai-message-avatar" style={{ background: "#f0f2f5", color: "#764ba2" }}>🤖</div>
                  <div className="ai-typing-dots">
                    <div className="ai-typing-dot" />
                    <div className="ai-typing-dot" />
                    <div className="ai-typing-dot" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="ai-input-area">
              <div className="ai-input-wrapper">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Ask Photentra AI anything..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                />
                <button className="ai-send-btn" onClick={sendMessage} disabled={!input.trim() || loading}>
                  ➤
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reminders & Appointments Panel */}
        {showPanel && (
          <div className="ai-panel">
            <div className="ai-panel-header">
              <div className="ai-panel-tabs">
                <button className={`ai-panel-tab ${panelTab === 'reminders' ? 'active' : ''}`} onClick={() => setPanelTab('reminders')}>
                  ⏰ Reminders {reminders.length > 0 && <span>({reminders.length})</span>}
                </button>
                <button className={`ai-panel-tab ${panelTab === 'appointments' ? 'active' : ''}`} onClick={() => setPanelTab('appointments')}>
                  📅 Appointments {appointments.length > 0 && <span>({appointments.length})</span>}
                </button>
              </div>
              <button className="ai-panel-close" onClick={() => setShowPanel(false)}>✕</button>
            </div>
            <div className="ai-panel-body">
              {panelTab === 'reminders' && (
                reminders.length === 0 ? (
                  <div className="ai-panel-empty">
                    <span>⏰</span>
                    <p>No reminders yet</p>
                    <p className="ai-panel-hint">Ask AI: "Remind me about editing tonight at 9"</p>
                  </div>
                ) : (
                  reminders.map(r => (
                    <div key={r.id} className={`ai-panel-card ${r.status === 'completed' ? 'completed' : ''}`}>
                      <div className="ai-panel-card-icon">⏰</div>
                      <div className="ai-panel-card-content">
                        <div className="ai-panel-card-title">{r.title}</div>
                        <div className="ai-panel-card-meta">
                          {r.reminder_date && <span>📅 {new Date(r.reminder_date).toLocaleDateString()}</span>}
                          {r.reminder_time && <span>🕐 {r.reminder_time.substring(0, 5)}</span>}
                        </div>
                        <div className={`ai-panel-card-status ${r.status}`}>{r.status}</div>
                      </div>
                    </div>
                  ))
                )
              )}
              {panelTab === 'appointments' && (
                appointments.length === 0 ? (
                  <div className="ai-panel-empty">
                    <span>📅</span>
                    <p>No appointments yet</p>
                    <p className="ai-panel-hint">Ask AI: "Book a shoot tomorrow at 5pm for Arjun"</p>
                  </div>
                ) : (
                  appointments.map(a => (
                    <div key={a.id} className={`ai-panel-card ${a.status === 'completed' ? 'completed' : ''}`}>
                      <div className="ai-panel-card-icon">📅</div>
                      <div className="ai-panel-card-content">
                        <div className="ai-panel-card-title">{a.title}</div>
                        {a.client_name && <div className="ai-panel-card-client">👤 {a.client_name}</div>}
                        <div className="ai-panel-card-meta">
                          {a.appointment_date && <span>📅 {new Date(a.appointment_date).toLocaleDateString()}</span>}
                          {a.appointment_time && <span>🕐 {a.appointment_time.substring(0, 5)}</span>}
                        </div>
                        <div className={`ai-panel-card-status ${a.status}`}>{a.status}</div>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhotentraAI;
